import { createFileRoute } from '@tanstack/react-router';
import { analyticsSearchSchema, buildDateRange } from '@/features/analytics/dateRange';
import { useAnalyticsBusiness } from '@/features/analytics/api';
import { KpiStrip } from '@/features/analytics/business/KpiStrip';
import { FunnelChart } from '@/features/analytics/business/FunnelChart';
import { ChurnChart } from '@/features/analytics/business/ChurnChart';
import { AiConsumptionChart } from '@/features/analytics/business/AiConsumptionChart';
import { RevenueByCurrencyTable } from '@/features/analytics/business/RevenueByCurrencyTable';

export const Route = createFileRoute('/_authed/analytics/business')({
  validateSearch: (raw: Record<string, unknown>) => {
    const parsed = analyticsSearchSchema.safeParse(raw);
    return parsed.success ? parsed.data : {};
  },
  component: BusinessPage,
});

function BusinessPage() {
  const rawSearch = Route.useSearch();
  const range = buildDateRange({ preset: rawSearch.preset, from: rawSearch.from, to: rawSearch.to });
  const { data: business, isLoading, isError } = useAnalyticsBusiness(range);

  if (isLoading) {
    return <div className="text-ink-soft">Loading…</div>;
  }

  if (isError || !business) {
    return (
      <div
        className="rounded-xl p-4 text-sm"
        style={{
          background: 'var(--color-error-bg)',
          border: '1px solid var(--color-error-border)',
          color: 'var(--color-error-text)',
        }}
      >
        Failed to load business analytics. The rollup data may not be available
        yet — try refreshing or triggering a manual rollup.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="analytics-business-page">
      <KpiStrip data={business} />

      <section>
        <h2 className="font-serif text-lg mb-3">Subscription funnel</h2>
        <FunnelChart funnel={business.funnel} />
      </section>

      <section>
        <h2 className="font-serif text-lg mb-3">Churn</h2>
        <ChurnChart daily={business.churn_daily} />
      </section>

      <section>
        <h2 className="font-serif text-lg mb-3">AI action consumption</h2>
        <AiConsumptionChart daily={business.ai_consumption_daily} />
      </section>

      <section>
        <h2 className="font-serif text-lg mb-3">Revenue by currency</h2>
        <RevenueByCurrencyTable summary={business.revenue_by_currency_summary} />
      </section>
    </div>
  );
}
